import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import { fetchThemeData, mergeThemeData } from '../../themeSlice';
import { selectAdminToken, selectOrganizationId, selectSessionId } from '../../sessionSlice';
import { updateThemeData } from '../../functions/updateThemeData';
import { s3PublicUrl, setThemeAssetBaseUrl } from '../../../utils/s3PublicUrl';
import ThemeAssetImg from '../../../components/ThemeAssetImg';
import {
  normalizeStage1,
  normalizeStagesDataForAdmin,
  defaultStage1Question,
  defaultStage1Topic,
  STAGE1_MIN_TOPICS,
  STAGE1_MAX_TOPICS,
  STAGE1_LIMITS,
  validateStage1,
  getStage1OptionAnswerLimit,
  STAGE2_MESSAGE_LIMIT,
  STAGE2_LIMITS,
  validateStage2,
  normalizeStage2,
  normalizeStage3,
  defaultStage3Sector,
  STAGE3_MIN_SECTORS,
  STAGE3_MAX_SECTORS,
  STAGE3_LIMITS,
  validateStage3,
  STAGE4_MIN_MAPS,
  STAGE4_MAX_MAPS,
  validateStage4,
  STAGE5_LIMITS,
  validateStage5,
  normalizeStage7,
  STAGE7_MAX_BACKGROUNDS,
  STAGE7_MAX_CLASS_BOARDS,
  STAGE7_MAX_TECHNOLOGIES,
  STAGE7_LIMITS,
  validateStage7,
} from './futureAdminNormalize';
import AdminTour from './AdminTour';
import './futureAdmin.css';

const TOUR_SEEN_KEY = 'fi_admin_tour_seen_v1';

const TOUR_STEPS = [
  {
    selector: '[data-tour="header"]',
    title: 'Welcome to the Content Admin',
    description:
      'This is where you edit all the text, images and videos shown in the Future India game. This quick tour points out the main areas.',
  },
  {
    selector: '[data-tour="tabs"]',
    title: 'Sections',
    description:
      'Each tab is one screen of the game — Intro, Stage 1 through 7, Thank You, and Page Backgrounds. Click a tab to open that screen for editing.',
  },
  {
    selector: '[data-tour="body"]',
    title: 'Edit content here',
    description:
      'Fill in text fields, upload images or videos, and add or remove repeatable items such as topics or pointers. Character limits are shown next to each field.',
  },
  {
    selector: '[data-tour="save"]',
    title: 'Save your changes',
    description:
      'Click "Save changes" to publish edits for the section you are viewing. Image and video uploads save automatically as soon as they finish uploading.',
  },
];

const STAGE_TABS = [
  { id: 'intro', label: 'Intro' },
  { id: 'stage_1', label: 'Stage 1 — Q&A' },
  { id: 'stage_2', label: 'Stage 2 — Planets' },
  { id: 'stage_3', label: 'Stage 3 — Economy' },
  { id: 'stage_4', label: 'Stage 4 — Maps' },
  { id: 'stage_5', label: 'Stage 5 — Statement' },
  { id: 'stage_6', label: 'Stage 6 — Jigsaw' },
  { id: 'stage_7', label: 'Stage 7 — School' },
  { id: 'thankyou', label: 'Thank You' },
  { id: 'backgrounds', label: 'Page Backgrounds' },
];

const VIDEO_EXTS = ['mp4', 'mov', 'mpeg', 'avi', 'mkv', 'webm', 'm4v'];
// Videos upload directly to S3 via presigned URL (bypasses API Gateway 10 MB cap).
const VIDEO_MAX_BYTES = 20 * 1024 * 1024;

const BG_DESKTOP_DIMS = { width: 1920, height: 1080 };
const BG_MOBILE_DIMS = { width: 414, height: 896 };

const INTRO_LIMITS = {
  startPopupText: 230,
  introLine: 100,
  introQuestion: 100,
  legacyText: 100,
};
const INTRO_PIN_DIMS = { width: 59, height: 70 };
// Stage 1 and Stage 6 don't have their own LIMITS objects (no other validated
// fields), so the shared 200-char popup-text cap lives here instead.
const STAGE_POPUP_TEXT_LIMIT = 200;
const STAGE6_TITLE_LIMIT = 100;
const STAGE6_PUZZLE_IMAGE_DIMS = { width: 911, height: 911 };
const THANKYOU_LETTER_IMAGE_DIMS = { width: 638, height: 500 };

function readImageDimensions(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read image dimensions'));
    };
    img.src = url;
  });
}

function readVideoDimensions(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const vid = document.createElement('video');
    vid.preload = 'metadata';
    vid.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve({ width: vid.videoWidth, height: vid.videoHeight });
    };
    vid.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read video dimensions'));
    };
    vid.src = url;
  });
}

/** Returns an error string if the media's pixel dimensions don't match, else null. */
async function checkExpectedDims(file, expectedDims, readDims) {
  if (!expectedDims) return null;
  let actual;
  try {
    actual = await readDims(file);
  } catch {
    return null; // Can't read dimensions (unsupported format) — don't block the upload on that.
  }
  if (actual.width !== expectedDims.width || actual.height !== expectedDims.height) {
    return `This field requires exactly ${expectedDims.width}x${expectedDims.height}px. ` +
      `The selected file is ${actual.width}x${actual.height}px.`;
  }
  return null;
}

const PAGE_BG_KEYS = [
  'intro',
  'intro_map',
  'stage_1',
  'stage_2',
  'stage_3',
  'stage_4',
  'stage_5',
  'stage_6',
  'stage_7',
  'thankyou',
];

const PAGE_BG_LABELS = {
  intro: 'Intro',
  intro_map: 'Intro Map',
  stage_1: 'Stage 1',
  stage_2: 'Stage 2',
  stage_3: 'Stage 3',
  stage_4: 'Stage 4',
  stage_5: 'Stage 5',
  stage_6: 'Stage 6',
  stage_7: 'Stage 7',
  thankyou: 'Thank You',
};

function emptyIntro() {
  return {
    text: '',
    start_popup_text: '',
    start_popup_image: '',
    next_button_image: '',
    heading1_image: '',
    heading2_image: '',
    intro_line1: '',
    intro_line2: '',
    intro_line3: '',
    intro_question: '',
    max_pins: 4,
    map_svg: 'introgame/modified_map.svg',
    pin_image: '',
  };
}

function emptyThankyou() {
  return { heading: '', pointer_points: [''], background_mob: '', letter_image: '' };
}

const THANKYOU_MAX_POINTERS = 10;
const THANKYOU_TEXT_LIMIT = 150;

function normalizeThankyou(raw) {
  const src = raw && typeof raw === 'object' ? raw : {};
  const fromPoints = Array.isArray(src.pointer_points) ? src.pointer_points : null;
  const fromText = typeof src.pointer_text === 'string' ? src.pointer_text : '';

  let pointer_points = fromPoints
    ? fromPoints.map((p) => String(p ?? '')).slice(0, THANKYOU_MAX_POINTERS)
    : fromText
      ? fromText
          .split(/\r?\n|•/g)
          .map((s) => s.trim())
          .filter(Boolean)
          .slice(0, THANKYOU_MAX_POINTERS)
      : [];

  if (pointer_points.length === 0) pointer_points = [''];

  return {
    ...src,
    heading: String(src.heading ?? ''),
    pointer_points,
  };
}

function CharCount({ value, max }) {
  const len = String(value ?? '').length;
  return (
    <span className={`fi-admin-char-count${len >= max ? ' fi-admin-char-count--limit' : ''}`}>
      {len}/{max}
    </span>
  );
}

const SECTION_LABELS = {
  intro: 'Intro',
  thankyou: 'Thank You',
  backgrounds: 'Page Backgrounds',
  stage_1: 'Stage 1',
  stage_2: 'Stage 2',
  stage_3: 'Stage 3',
  stage_4: 'Stage 4',
  stage_5: 'Stage 5',
  stage_6: 'Stage 6',
  stage_7: 'Stage 7',
};

// Prints once whenever this module is evaluated (page load / bundle parse),
// independent of any user action — if this line never shows up in the
// console, the browser is still running an old bundle, not this code.
// eslint-disable-next-line no-console
console.log('[futureAdmin.jsx] BUILD_MARKER=v3-video-presigned loaded');

export default function FutureAdmin() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const { currentTheme, data, status } = useSelector((state) => state.theme);

  const [tab, setTab] = useState('intro');
  const [tourActive, setTourActive] = useState(false);
  const [savingSection, setSavingSection] = useState(null);
  const [saveStatus, setSaveStatus] = useState({});
  const [intro, setIntro] = useState(emptyIntro());
  const [thankyou, setThankyou] = useState(emptyThankyou());
  const [pagesBg, setPagesBg] = useState({});
  const [stagesData, setStagesData] = useState({});
  const hydratedThemeRef = useRef(null);
  const skipNextHydrateRef = useRef(false);
  const prevThemeStatusRef = useRef(status);
  const autoSaveQueueRef = useRef([]);
  const autoSaveRunningRef = useRef(false);

  useEffect(() => {
    hydratedThemeRef.current = null;
    dispatch(fetchThemeData({ themeId: currentTheme || null, isAdmin: true }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (data?.asset_base_url) setThemeAssetBaseUrl(data.asset_base_url);
  }, [data?.asset_base_url]);

  const hydrateFromData = (source) => {
    if (!source) return;
    setIntro({ ...emptyIntro(), ...(source.intro || {}) });
    setThankyou({ ...emptyThankyou(), ...normalizeThankyou(source.thankyou || {}) });
    setPagesBg({ ...(source.pages_bg || {}) });
    setStagesData(normalizeStagesDataForAdmin(source.stages_data || {}));
  };

  useEffect(() => {
    const prevStatus = prevThemeStatusRef.current;
    prevThemeStatusRef.current = status;
    if (!data) return;
    // Wait for the in-flight fetch to finish — otherwise we hydrate from
    // stale Redux cache (e.g. after navigating back from another page) and
    // then permanently skip the fresh response for the same theme.
    if (status !== 'succeeded') return;
    if (skipNextHydrateRef.current) {
      skipNextHydrateRef.current = false;
      return;
    }
    const becameSucceeded = prevStatus !== 'succeeded';
    const themeKey = String(data.themename || currentTheme || '');
    // Only hydrate when a fetch just completed (or theme identity changed).
    // Ignore Redux patches from mergeThemeData after save so local edits stay.
    if (!becameSucceeded && hydratedThemeRef.current === themeKey) return;
    hydratedThemeRef.current = themeKey;
    hydrateFromData(data);
  }, [data, currentTheme, status]);

  useEffect(() => {
    if (tab === 'stage_1') {
      setStagesData((prev) => ({
        ...prev,
        stage_1: normalizeStage1(prev.stage_1),
      }));
    }
    if (tab === 'stage_2') {
      setStagesData((prev) => ({
        ...prev,
        stage_2: normalizeStage2(prev.stage_2),
      }));
    }
    if (tab === 'stage_3') {
      setStagesData((prev) => ({
        ...prev,
        stage_3: normalizeStage3(prev.stage_3),
      }));
    }
    if (tab === 'stage_7') {
      setStagesData((prev) => ({
        ...prev,
        stage_7: normalizeStage7(prev.stage_7),
      }));
    }
  }, [tab]);

  useEffect(() => {
    if (status !== 'succeeded') return;
    try {
      if (!window.localStorage.getItem(TOUR_SEEN_KEY)) setTourActive(true);
    } catch {
      // localStorage can throw in locked-down browser contexts — just skip
      // the auto-tour rather than break the admin page over it.
    }
  }, [status]);

  const closeTour = () => {
    setTourActive(false);
    try {
      window.localStorage.setItem(TOUR_SEEN_KEY, '1');
    } catch {
      // Non-fatal — worst case the tour auto-shows again next visit.
    }
  };

  const markSectionDirty = (sectionId) => {
    setSaveStatus((prev) => {
      if (prev[sectionId] !== 'saved') return prev;
      const next = { ...prev };
      delete next[sectionId];
      return next;
    });
  };

  const stageKey = tab.startsWith('stage_') ? tab : null;
  const stage = stageKey === 'stage_1'
    ? normalizeStage1(stagesData.stage_1)
    : stageKey === 'stage_2'
      ? normalizeStage2(stagesData.stage_2)
      : stageKey === 'stage_3'
      ? normalizeStage3(stagesData.stage_3)
      : stageKey === 'stage_7'
        ? normalizeStage7(stagesData.stage_7)
        : stageKey
          ? stagesData[stageKey] || {}
          : {};

  const setStage1 = (nextStage1) => {
    markSectionDirty('stage_1');
    setStagesData((prev) => ({ ...prev, stage_1: normalizeStage1(nextStage1) }));
  };

  const setStage2 = (nextStage2) => {
    markSectionDirty('stage_2');
    setStagesData((prev) => ({ ...prev, stage_2: normalizeStage2(nextStage2) }));
  };

  const setStage3 = (nextStage3) => {
    markSectionDirty('stage_3');
    setStagesData((prev) => ({ ...prev, stage_3: normalizeStage3(nextStage3) }));
  };

  const setStage7 = (nextStage7) => {
    markSectionDirty('stage_7');
    setStagesData((prev) => ({ ...prev, stage_7: normalizeStage7(nextStage7) }));
  };

  const updateStage7List = (listKey, index, patch) => {
    const list = stage[listKey] || [];
    const nextList = list.map((item, i) => (i === index ? { ...item, ...patch } : item));
    setStage7({ ...stage, [listKey]: nextList });
  };

  const updateStage = (partial) => {
    if (!stageKey) return;
    if (stageKey === 'stage_1') {
      setStage1({ ...stage, ...partial });
      return;
    }
    if (stageKey === 'stage_2') {
      setStage2({ ...stage, ...partial });
      return;
    }
    if (stageKey === 'stage_3') {
      setStage3({ ...stage, ...partial });
      return;
    }
    if (stageKey === 'stage_7') {
      setStage7({ ...stage, ...partial });
      return;
    }
    markSectionDirty(stageKey);
    setStagesData((prev) => ({ ...prev, [stageKey]: { ...prev[stageKey], ...partial } }));
  };

  const uploadMedia = async (file, fileKey = 'image') => {
    if (!file || !adminToken) return null;
    // eslint-disable-next-line no-console
    console.log('[uploadMedia][BUILD_MARKER=v2-video-9mb-cap] start', {
      fileKey,
      name: file.name,
      sizeBytes: file.size,
      sizeMB: (file.size / (1024 * 1024)).toFixed(2),
      type: file.type,
      backendUrl: process.env.REACT_APP_BACKEND_URL,
    });
    const form = new FormData();
    form.append('organizationId', organizationId);
    form.append('sessionId', sessionId);
    form.append('themeName', currentTheme || data?.themename);
    form.append(fileKey, file);
    let resp;
    try {
      resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/updateThemeMedia`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${adminToken}` },
        body: form,
      });
    } catch (networkErr) {
      // "Failed to fetch" here means the request never got a response at all
      // (blocked before/without reaching the server, e.g. a gateway-level
      // rejection whose error response carries no CORS headers) — distinct
      // from the server responding with a non-2xx status.
      console.error('[uploadMedia] network-level failure (request never completed)', networkErr);
      throw networkErr;
    }
    console.log('[uploadMedia] response received', { status: resp.status, statusText: resp.statusText, ok: resp.ok });
    const json = await resp.json().catch((parseErr) => {
      console.error('[uploadMedia] failed to parse response JSON', parseErr);
      return {};
    });
    console.log('[uploadMedia] response body', json);
    if (!resp.ok || !json.success) throw new Error(json.message || 'Upload failed');
    if (json.asset_base_url) setThemeAssetBaseUrl(json.asset_base_url);
    return json.uploaded?.[fileKey] || null;
  };

  const uploadImage = (file) => uploadMedia(file, 'image');

  const uploadVideo = async (file) => {
    if (!file || !adminToken) return null;
    const form = new FormData();
    form.append('organizationId', organizationId);
    form.append('sessionId', sessionId);
    form.append('themeName', currentTheme || data?.themename);
    form.append('videoFileName', file.name);
    form.append('videoFileType', file.type || 'video/mp4');
    form.append('videoFileSize', String(file.size));

    let resp;
    try {
      resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/updateThemeMedia`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${adminToken}` },
        body: form,
      });
    } catch (networkErr) {
      console.error('[uploadVideo] network-level failure', networkErr);
      throw networkErr;
    }

    const json = await resp.json().catch(() => ({}));
    if (!resp.ok || !json.success || !json.presignedUpload) {
      throw new Error(json.message || 'Failed to get upload URL');
    }

    // ACL is already baked into the presigned URL's query string
    // (`x-amz-acl=public-read`) — the URL's `X-Amz-SignedHeaders` lists only
    // `host`, so re-sending it as a header here is redundant and, in some S3
    // configurations, a duplicate ACL directive (query + header) is itself
    // what triggers a 403, independent of any signature mismatch.
    const s3Resp = await fetch(json.presignedUpload.uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type || 'video/mp4',
      },
      body: file,
    });
    if (!s3Resp.ok) {
      const bodyText = await s3Resp.text().catch(() => '');
      console.error('[uploadVideo] S3 PUT rejected', {
        status: s3Resp.status,
        statusText: s3Resp.statusText,
        body: bodyText,
      });
      throw new Error(`Failed to upload video to S3 (${s3Resp.status}): ${bodyText.slice(0, 400)}`);
    }

    if (json.asset_base_url) setThemeAssetBaseUrl(json.asset_base_url);
    return json.uploaded?.video || json.presignedUpload.filename || null;
  };

  const themeName = currentTheme || data?.themename;

  const buildPayloadForSection = (sectionId) => {
    const base = {
      organizationId: organizationId || 'admin',
      sessionId: sessionId || 'admin',
      currentTheme: themeName,
    };
    if (sectionId === 'intro') {
      return { ...base, data: { intro, gameType: 'future_india' } };
    }
    if (sectionId === 'thankyou') {
      return { ...base, data: { thankyou, gameType: 'future_india' } };
    }
    if (sectionId === 'backgrounds') {
      return { ...base, data: { pages_bg: pagesBg, gameType: 'future_india' } };
    }
    if (sectionId.startsWith('stage_')) {
      const stagePayload =
        sectionId === 'stage_1'
          ? normalizeStage1(stagesData.stage_1)
          : sectionId === 'stage_2'
            ? normalizeStage2(stagesData.stage_2)
            : sectionId === 'stage_3'
            ? normalizeStage3(stagesData.stage_3)
            : sectionId === 'stage_7'
              ? normalizeStage7(stagesData.stage_7)
              : stagesData[sectionId] || {};
      return {
        ...base,
        data: {
          stages_data: {
            [sectionId]: stagePayload,
          },
          gameType: 'future_india',
        },
      };
    }
    return null;
  };

  const applySectionToLocalState = (sectionId, payload) => {
    const sectionData = payload?.data || {};
    if (sectionId === 'intro' && sectionData.intro) {
      setIntro({ ...emptyIntro(), ...sectionData.intro });
    }
    if (sectionId === 'thankyou' && sectionData.thankyou) {
      setThankyou({ ...emptyThankyou(), ...normalizeThankyou(sectionData.thankyou) });
    }
    if (sectionId === 'backgrounds' && sectionData.pages_bg) {
      setPagesBg({ ...sectionData.pages_bg });
    }
    if (sectionId.startsWith('stage_') && sectionData.stages_data?.[sectionId]) {
      const raw = sectionData.stages_data[sectionId];
      const nextStage =
        sectionId === 'stage_1'
          ? normalizeStage1(raw)
          : sectionId === 'stage_2'
            ? normalizeStage2(raw)
            : sectionId === 'stage_3'
            ? normalizeStage3(raw)
            : sectionId === 'stage_7'
              ? normalizeStage7(raw)
              : raw;
      setStagesData((prev) => ({ ...prev, [sectionId]: nextStage }));
    }
  };

  const handleSaveSection = async (sectionId, { skipValidation = false } = {}) => {
    if (!adminToken) {
      Swal.fire('Error', 'Session expired. Please log in again.', 'error');
      return;
    }

    if (!skipValidation && sectionId === 'stage_1') {
      const { valid, errors } = validateStage1(stagesData.stage_1);
      if (!valid) {
        Swal.fire({
          icon: 'warning',
          title: 'Cannot save',
          html: `<div class="fi-admin-validation-list">${errors.map((e) => `• ${e}`).join('<br>')}</div>`,
        });
        return;
      }
    }

    if (!skipValidation && sectionId === 'stage_2') {
      const { valid, errors } = validateStage2(stagesData.stage_2);
      if (!valid) {
        Swal.fire({
          icon: 'warning',
          title: 'Cannot save',
          html: `<div class="fi-admin-validation-list">${errors.map((e) => `• ${e}`).join('<br>')}</div>`,
        });
        return;
      }
    }

    if (!skipValidation && sectionId === 'stage_3') {
      const { valid, errors } = validateStage3(stagesData.stage_3);
      if (!valid) {
        Swal.fire({
          icon: 'warning',
          title: 'Cannot save',
          html: `<div class="fi-admin-validation-list">${errors.map((e) => `• ${e}`).join('<br>')}</div>`,
        });
        return;
      }
    }

    if (!skipValidation && sectionId === 'stage_4') {
      const { valid, errors } = validateStage4(stagesData.stage_4);
      if (!valid) {
        Swal.fire({
          icon: 'warning',
          title: 'Cannot save',
          html: `<div class="fi-admin-validation-list">${errors.map((e) => `• ${e}`).join('<br>')}</div>`,
        });
        return;
      }
    }

    if (!skipValidation && sectionId === 'stage_5') {
      const { valid, errors } = validateStage5(stagesData.stage_5);
      if (!valid) {
        Swal.fire({
          icon: 'warning',
          title: 'Cannot save',
          html: `<div class="fi-admin-validation-list">${errors.map((e) => `• ${e}`).join('<br>')}</div>`,
        });
        return;
      }
    }

    if (!skipValidation && sectionId === 'stage_7') {
      const { valid, errors } = validateStage7(stagesData.stage_7);
      if (!valid) {
        Swal.fire({
          icon: 'warning',
          title: 'Cannot save',
          html: `<div class="fi-admin-validation-list">${errors.map((e) => `• ${e}`).join('<br>')}</div>`,
        });
        return;
      }
    }

    const payload = buildPayloadForSection(sectionId);
    if (!payload) return;

    setSavingSection(sectionId);
    setSaveStatus((prev) => ({ ...prev, [sectionId]: 'saving' }));
    try {
      await updateThemeData({ token: adminToken, payload });
      skipNextHydrateRef.current = true;
      applySectionToLocalState(sectionId, payload);
      // Keep Redux in sync so game / other pages don't keep serving pre-save data.
      if (payload?.data) {
        dispatch(mergeThemeData(payload.data));
      }
      setSaveStatus((prev) => ({ ...prev, [sectionId]: 'saved' }));
      Swal.fire({
        icon: 'success',
        title: 'Saved',
        text: `${SECTION_LABELS[sectionId] || 'Section'} saved successfully.`,
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (e) {
      setSaveStatus((prev) => ({ ...prev, [sectionId]: 'error' }));
      Swal.fire('Error', e.message || 'Failed to save.', 'error');
    } finally {
      setSavingSection(null);
    }
  };

  const handleSaveSectionRef = useRef(handleSaveSection);
  handleSaveSectionRef.current = handleSaveSection;

  // Image uploads must persist immediately — uploading only writes the file to S3 and
  // updates local preview state; without this, refreshing before clicking "Save changes"
  // silently loses the upload. Auto-saves are queued and run strictly one at a time: each
  // save does a read-merge-write of the shared stages_data row on the backend, so firing
  // them concurrently (e.g. uploading across several stage tabs in quick succession) lets
  // a later save's read miss an earlier save's still-in-flight write, silently dropping
  // whichever stage that stale read didn't see.
  const processAutoSaveQueue = async () => {
    if (autoSaveRunningRef.current) return;
    autoSaveRunningRef.current = true;
    try {
      while (autoSaveQueueRef.current.length > 0) {
        const sectionId = autoSaveQueueRef.current.shift();
        await handleSaveSectionRef.current(sectionId, { skipValidation: true });
      }
    } finally {
      autoSaveRunningRef.current = false;
    }
  };

  const queueAutoSave = (sectionId) => {
    autoSaveQueueRef.current.push(sectionId);
    processAutoSaveQueue();
  };

  const SectionSaveBar = ({ sectionId }) => {
    const isSaving = savingSection === sectionId || saveStatus[sectionId] === 'saving';
    const isError = saveStatus[sectionId] === 'error';
    return (
      <div className="fi-admin-save-bar">
        <button
          type="button"
          className="fi-admin-save fi-admin-save--section"
          data-tour="save"
          disabled={Boolean(savingSection)}
          onClick={() => handleSaveSection(sectionId)}
        >
          {isSaving ? 'Saving…' : 'Save changes'}
        </button>
        {isError ? <span className="fi-admin-error-msg">Save failed — try again</span> : null}
      </div>
    );
  };

  const ImageField = ({
    label,
    value,
    onChange,
    accept = 'image/*',
    fileKey = 'image',
    compact = false,
    expectedDims = null,
    showFilename = true,
  }) => (
    <div className={`fi-admin-field fi-admin-image-field${compact ? ' fi-admin-image-field--compact' : ''}`}>
      <label>
        {label}
        {expectedDims ? <span className="fi-admin-hint"> (exactly {expectedDims.width}x{expectedDims.height}px)</span> : null}
      </label>
      {value ? (
        <ThemeAssetImg
          asset={value}
          resolveUrl={s3PublicUrl}
          className="fi-admin-preview"
        />
      ) : null}
      <input
        type="file"
        accept={accept}
        onChange={async (e) => {
          const f = e.target.files?.[0];
          if (!f) return;
          // `accept="image/*"` only filters the file picker's default view — it
          // doesn't block a video from being selected or dragged in. Without this
          // guard, a video picked here goes through the old multipart upload path,
          // hits the Lambda/API Gateway 10 MB payload cap, and fails with a 413
          // that the browser reports as a confusing CORS error.
          const ext = String(f.name || '').split('.').pop().toLowerCase();
          if (VIDEO_EXTS.includes(ext)) {
            Swal.fire(
              'Wrong field',
              "This field only accepts images. Use the \"Desktop video\" field below to upload a video.",
              'warning'
            );
            e.target.value = '';
            return;
          }
          const dimsError = await checkExpectedDims(f, expectedDims, readImageDimensions);
          if (dimsError) {
            Swal.fire('Wrong dimensions', dimsError, 'warning');
            e.target.value = '';
            return;
          }
          try {
            const name = fileKey === 'image' ? await uploadImage(f) : await uploadMedia(f, fileKey);
            if (name) {
              onChange(name);
              // onChange() only schedules a state update — React hasn't re-rendered yet,
              // so handleSaveSectionRef still points at the stale closure from before this
              // upload and would auto-save without the new image (a real incident: repeat
              // uploads on stage_7 showed a "Saved" popup but silently kept the old image).
              // Deferring to a macrotask lets the pending render/commit flush first.
              setTimeout(() => queueAutoSave(tab), 0);
            }
          } catch (err) {
            Swal.fire('Upload failed', err.message, 'error');
          }
          e.target.value = '';
        }}
      />
      {value && showFilename ? <span className="fi-admin-image-field__filename">{value}</span> : null}
    </div>
  );

  const VideoField = ({ label, value, onChange, compact = false, expectedDims = null }) => (
    <div className={`fi-admin-field fi-admin-image-field${compact ? ' fi-admin-image-field--compact' : ''}`}>
      <label>
        {label}
        {expectedDims ? <span className="fi-admin-hint"> (exactly {expectedDims.width}x{expectedDims.height}px)</span> : null}
      </label>
      {value ? (
        <video src={s3PublicUrl(value)} className="fi-admin-preview" muted controls />
      ) : null}
      <input
        type="file"
        accept="video/mp4,.mp4"
        onChange={async (e) => {
          const f = e.target.files?.[0];
          if (!f) return;
          const ext = String(f.name || '').split('.').pop().toLowerCase();
          console.log('[VideoField][BUILD_MARKER=v3-video-presigned] file selected', {
            name: f.name,
            ext,
            sizeBytes: f.size,
            sizeMB: (f.size / (1024 * 1024)).toFixed(2),
            maxAllowedBytes: VIDEO_MAX_BYTES,
            maxAllowedMB: (VIDEO_MAX_BYTES / (1024 * 1024)).toFixed(2),
          });
          if (ext !== 'mp4') {
            console.warn('[VideoField] rejected: wrong extension', ext);
            Swal.fire('Wrong file type', 'This field only accepts .mp4 video files.', 'warning');
            e.target.value = '';
            return;
          }
          if (f.size > VIDEO_MAX_BYTES) {
            console.warn('[VideoField] rejected: file too large', { sizeBytes: f.size, maxAllowedBytes: VIDEO_MAX_BYTES });
            Swal.fire('File too large', 'Video must be under 20 MB.', 'warning');
            e.target.value = '';
            return;
          }
          const dimsError = await checkExpectedDims(f, expectedDims, readVideoDimensions);
          if (dimsError) {
            Swal.fire('Wrong dimensions', dimsError, 'warning');
            e.target.value = '';
            return;
          }
          console.log('[VideoField] passed client-side checks, uploading…');
          try {
            const name = await uploadVideo(f);
            console.log('[VideoField] upload result', { name });
            if (name) {
              onChange(name);
              setTimeout(() => queueAutoSave(tab), 0);
            }
          } catch (err) {
            console.error('[VideoField] upload failed', err);
            Swal.fire('Upload failed', err.message, 'error');
          }
          e.target.value = '';
        }}
      />
      {value ? (
        <>
          <span className="fi-admin-image-field__filename">{value}</span>
          <button
            type="button"
            className="fi-admin-inline-btn"
            onClick={() => {
              onChange('');
              setTimeout(() => queueAutoSave(tab), 0);
            }}
          >
            Remove video
          </button>
        </>
      ) : null}
    </div>
  );

  if (status === 'loading') {
    return <div className="fi-admin-loading">Loading theme…</div>;
  }

  return (
    <div className="fi-admin">
      <div className="fi-admin-header" data-tour="header">
        <button type="button" onClick={() => navigate('/admin')}>← Themes</button>
        <h1>Future India — Content Admin</h1>
        <button type="button" className="fi-admin-tour-btn" onClick={() => setTourActive(true)}>
          Take a tour
        </button>
      </div>
      <nav className="fi-admin-tabs" data-tour="tabs">
        {STAGE_TABS.map((t) => (
          <button key={t.id} type="button" className={tab === t.id ? 'active' : ''} onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
      </nav>
      <div className="fi-admin-body" data-tour="body">
        {tab === 'intro' && (
          <>
            <div className="fi-admin-field">
              <label>Start popup text (optional overlay) <CharCount value={intro.start_popup_text} max={INTRO_LIMITS.startPopupText} /></label>
              <input
                maxLength={INTRO_LIMITS.startPopupText}
                value={intro.start_popup_text}
                onChange={(e) => { markSectionDirty('intro'); setIntro({ ...intro, start_popup_text: e.target.value }); }}
              />
            </div>
            <div className="fi-admin-field">
              <label>Map intro line 1 (text fallback) <CharCount value={intro.intro_line1} max={INTRO_LIMITS.introLine} /></label>
              <input
                maxLength={INTRO_LIMITS.introLine}
                value={intro.intro_line1 || ''}
                onChange={(e) => { markSectionDirty('intro'); setIntro({ ...intro, intro_line1: e.target.value }); }}
              />
            </div>
            <div className="fi-admin-field">
              <label>Map intro line 2 <CharCount value={intro.intro_line2} max={INTRO_LIMITS.introLine} /></label>
              <input
                maxLength={INTRO_LIMITS.introLine}
                value={intro.intro_line2 || ''}
                onChange={(e) => { markSectionDirty('intro'); setIntro({ ...intro, intro_line2: e.target.value }); }}
              />
            </div>
            <div className="fi-admin-field">
              <label>Map intro line 3 <CharCount value={intro.intro_line3} max={INTRO_LIMITS.introLine} /></label>
              <input
                maxLength={INTRO_LIMITS.introLine}
                value={intro.intro_line3 || ''}
                onChange={(e) => { markSectionDirty('intro'); setIntro({ ...intro, intro_line3: e.target.value }); }}
              />
            </div>
            <div className="fi-admin-field">
              <label>Map question (bold line) <CharCount value={intro.intro_question} max={INTRO_LIMITS.introQuestion} /></label>
              <input
                maxLength={INTRO_LIMITS.introQuestion}
                value={intro.intro_question || ''}
                onChange={(e) => { markSectionDirty('intro'); setIntro({ ...intro, intro_question: e.target.value }); }}
              />
            </div>
            <div className="fi-admin-field">
              <label>Intro text (legacy) <CharCount value={intro.text} max={INTRO_LIMITS.legacyText} /></label>
              <textarea
                maxLength={INTRO_LIMITS.legacyText}
                value={intro.text}
                onChange={(e) => { markSectionDirty('intro'); setIntro({ ...intro, text: e.target.value }); }}
                rows={3}
              />
            </div>
            <div className="fi-admin-field"><label>Max map pins (max 5)</label><input type="number" min={1} max={5} value={intro.max_pins} onChange={(e) => { markSectionDirty('intro'); const clamped = Math.min(5, Math.max(1, Number(e.target.value) || 1)); setIntro({ ...intro, max_pins: clamped }); }} /></div>
            <ImageField
              label="Pin image"
              value={intro.pin_image}
              expectedDims={INTRO_PIN_DIMS}
              accept="image/png,.png"
              onChange={(v) => { markSectionDirty('intro'); setIntro({ ...intro, pin_image: v }); }}
              compact
            />
            <ImageField
              label="Map SVG"
              value={intro.map_svg}
              accept=".svg,image/svg+xml"
              fileKey="map_svg"
              onChange={(v) => { markSectionDirty('intro'); setIntro({ ...intro, map_svg: v }); }}
            />
            <p className="fi-admin-hint">Intro backgrounds are set under the Page Backgrounds tab (intro / intro_map).</p>
            <SectionSaveBar sectionId="intro" />
          </>
        )}

        {tab === 'thankyou' && (
          <>
            <div className="fi-admin-field">
              <label>Heading <CharCount value={thankyou.heading} max={THANKYOU_TEXT_LIMIT} /></label>
              <input
                maxLength={THANKYOU_TEXT_LIMIT}
                value={thankyou.heading}
                onChange={(e) => { markSectionDirty('thankyou'); setThankyou({ ...thankyou, heading: e.target.value }); }}
              />
            </div>
            <p className="fi-admin-hint">Pointer points: max {THANKYOU_MAX_POINTERS}, {THANKYOU_TEXT_LIMIT} characters each.</p>
            {(thankyou.pointer_points || []).map((p, i) => (
              <div key={i} className="fi-admin-field">
                <label>Pointer {i + 1} <CharCount value={p} max={THANKYOU_TEXT_LIMIT} /></label>
                <div className="fi-admin-pointer-row">
                  <input
                    maxLength={THANKYOU_TEXT_LIMIT}
                    value={p}
                    onChange={(e) => {
                      markSectionDirty('thankyou');
                      const next = [...(thankyou.pointer_points || [])];
                      next[i] = e.target.value;
                      setThankyou({ ...thankyou, pointer_points: next });
                    }}
                  />
                  <button
                    type="button"
                    className="fi-admin-icon-btn fi-admin-icon-btn--delete"
                    title="Remove pointer"
                    aria-label={`Remove pointer ${i + 1}`}
                    onClick={() => {
                      markSectionDirty('thankyou');
                      const filtered = (thankyou.pointer_points || []).filter((_, idx) => idx !== i);
                      setThankyou({
                        ...thankyou,
                        pointer_points: filtered.length > 0 ? filtered : [''],
                      });
                    }}
                  >
                    ×
                  </button>
                </div>
              </div>
            ))}
            {(thankyou.pointer_points || []).length < THANKYOU_MAX_POINTERS ? (
              <button
                type="button"
                className="fi-admin-inline-btn"
                onClick={() => {
                  markSectionDirty('thankyou');
                  setThankyou({ ...thankyou, pointer_points: [...(thankyou.pointer_points || []), ''] });
                }}
              >
                + Add pointer
              </button>
            ) : null}
            <ImageField
              label="Letter image"
              value={thankyou.letter_image}
              expectedDims={THANKYOU_LETTER_IMAGE_DIMS}
              onChange={(v) => { markSectionDirty('thankyou'); setThankyou({ ...thankyou, letter_image: v }); }}
              showFilename={false}
            />
            <SectionSaveBar sectionId="thankyou" />
          </>
        )}

        {tab === 'backgrounds' && (
          <>
            {PAGE_BG_KEYS.map((k) => (
              <div key={k} className="fi-admin-bg-row">
                <h3 className="fi-admin-bg-row__title">{PAGE_BG_LABELS[k] || k}</h3>
                {k === 'intro' ? (
                  <div className="fi-admin-bg-row__fields">
                    <VideoField
                      compact
                      label="Desktop video, max 20 MB (desktop only, never shown on mobile — if empty, the main theme background is used on both desktop and mobile)"
                      value={pagesBg.intro_desk_video}
                      onChange={(v) => {
                        markSectionDirty('backgrounds');
                        setPagesBg({ ...pagesBg, intro_desk_video: v });
                      }}
                    />
                  </div>
                ) : (
                  <div className="fi-admin-bg-row__fields">
                    <ImageField
                      compact
                      label="Desktop"
                      value={pagesBg[`${k}_desk`]}
                      expectedDims={BG_DESKTOP_DIMS}
                      onChange={(v) => {
                        markSectionDirty('backgrounds');
                        setPagesBg({ ...pagesBg, [`${k}_desk`]: v });
                      }}
                    />
                    <ImageField
                      compact
                      label="Mobile"
                      value={pagesBg[`${k}_mob`]}
                      expectedDims={BG_MOBILE_DIMS}
                      onChange={(v) => {
                        markSectionDirty('backgrounds');
                        setPagesBg({ ...pagesBg, [`${k}_mob`]: v });
                      }}
                    />
                  </div>
                )}
              </div>
            ))}
            <SectionSaveBar sectionId="backgrounds" />
          </>
        )}

        {stageKey === 'stage_1' && (
          <>
            <div className="fi-admin-field">
              <label>Start popup text <CharCount value={stage.start_popup_text} max={STAGE_POPUP_TEXT_LIMIT} /></label>
              <input
                maxLength={STAGE_POPUP_TEXT_LIMIT}
                value={stage.start_popup_text || ''}
                onChange={(e) => updateStage({ start_popup_text: e.target.value })}
              />
            </div>
            <div className="fi-admin-field">
              <label>End popup text <CharCount value={stage.end_popup_text} max={STAGE_POPUP_TEXT_LIMIT} /></label>
              <input
                maxLength={STAGE_POPUP_TEXT_LIMIT}
                value={stage.end_popup_text || ''}
                onChange={(e) => updateStage({ end_popup_text: e.target.value })}
              />
            </div>
            <p className="fi-admin-hint">
              Topics: min {STAGE1_MIN_TOPICS}, max {STAGE1_MAX_TOPICS}. Each topic has one question with 2 options.
              All fields are required. Limits: topic {STAGE1_LIMITS.topicHeading}, question {STAGE1_LIMITS.questionHeading},
              option 1 answer {STAGE1_LIMITS.option1Answer}, option 2 answer {STAGE1_LIMITS.option2Answer},
              description {STAGE1_LIMITS.optionDescription} characters.
            </p>
            {stage.topics.map((topic, ti) => {
              const q = topic.questions?.[0] || defaultStage1Question();
              const updateQuestion = (patch) => {
                const topics = stage.topics.map((t, i) => {
                  if (i !== ti) return t;
                  return { ...t, questions: [{ ...q, ...patch }] };
                });
                setStage1({ ...stage, topics });
              };
              const updateOption = (oi, patch) => {
                const options = [...(q.options || [{ answer: '', description: '' }, { answer: '', description: '' }])];
                options[oi] = { ...options[oi], ...patch };
                updateQuestion({ options });
              };
              return (
              <div key={ti} className="fi-admin-card">
                <div className="fi-admin-card-header">
                  <h3>Topic {ti + 1}</h3>
                  {stage.topics.length > STAGE1_MIN_TOPICS ? (
                    <button
                      type="button"
                      className="fi-admin-remove-btn"
                      onClick={() => {
                        setStage1({ ...stage, topics: stage.topics.filter((_, i) => i !== ti) });
                      }}
                    >
                      Remove topic
                    </button>
                  ) : null}
                </div>
                <div className="fi-admin-field">
                  <label>
                    Topic {ti + 1} heading <CharCount value={topic.name} max={STAGE1_LIMITS.topicHeading} />
                  </label>
                  <input
                    maxLength={STAGE1_LIMITS.topicHeading}
                    value={topic.name || ''}
                    onChange={(e) => {
                      const topics = stage.topics.map((t, i) =>
                        i === ti ? { ...t, name: e.target.value } : t
                      );
                      setStage1({ ...stage, topics });
                    }}
                  />
                </div>
                <div className="fi-admin-subcard">
                  <div className="fi-admin-field">
                    <label>
                      Question heading <CharCount value={q.heading} max={STAGE1_LIMITS.questionHeading} />
                    </label>
                    <input
                      maxLength={STAGE1_LIMITS.questionHeading}
                      value={q.heading || ''}
                      onChange={(e) => updateQuestion({ heading: e.target.value })}
                    />
                  </div>
                  {[0, 1].map((oi) => {
                    const opt = q.options?.[oi] || { answer: '', description: '' };
                    const answerMax = getStage1OptionAnswerLimit(oi);
                    return (
                      <div key={oi} className="fi-admin-option-block">
                        <div className="fi-admin-field">
                          <label>
                            Option {oi + 1} — answer <CharCount value={opt.answer} max={answerMax} />
                          </label>
                          <input
                            maxLength={answerMax}
                            value={opt.answer || ''}
                            onChange={(e) => updateOption(oi, { answer: e.target.value })}
                          />
                        </div>
                        <div className="fi-admin-field">
                          <label>
                            Option {oi + 1} — description{' '}
                            <CharCount value={opt.description} max={STAGE1_LIMITS.optionDescription} />
                          </label>
                          <textarea
                            rows={2}
                            maxLength={STAGE1_LIMITS.optionDescription}
                            value={opt.description || ''}
                            onChange={(e) => updateOption(oi, { description: e.target.value })}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
            })}
            {stage.topics.length < STAGE1_MAX_TOPICS ? (
              <button
                type="button"
                className="fi-admin-inline-btn"
                onClick={() => {
                  setStage1({
                    ...stage,
                    topics: [...stage.topics, defaultStage1Topic(stage.topics.length)],
                  });
                }}
              >
                + Add topic (Topic {stage.topics.length + 1})
              </button>
            ) : null}
            <SectionSaveBar sectionId="stage_1" />
          </>
        )}

        {stageKey === 'stage_2' && (
          <>
            <div className="fi-admin-field">
              <label>Heading <CharCount value={stage.heading} max={STAGE2_LIMITS.heading} /></label>
              <input
                maxLength={STAGE2_LIMITS.heading}
                value={stage.heading || ''}
                onChange={(e) => updateStage({ heading: e.target.value })}
              />
            </div>
            <div className="fi-admin-field">
              <label>Subheading <CharCount value={stage.subheading} max={STAGE2_LIMITS.subheading} /></label>
              <input
                maxLength={STAGE2_LIMITS.subheading}
                value={stage.subheading || ''}
                onChange={(e) => updateStage({ subheading: e.target.value })}
              />
            </div>
            <div className="fi-admin-field">
              <label>Start popup text <CharCount value={stage.start_popup_text} max={STAGE2_LIMITS.popupText} /></label>
              <input
                maxLength={STAGE2_LIMITS.popupText}
                value={stage.start_popup_text || ''}
                onChange={(e) => updateStage({ start_popup_text: e.target.value })}
              />
            </div>
            <div className="fi-admin-field">
              <label>End popup text <CharCount value={stage.end_popup_text} max={STAGE2_LIMITS.popupText} /></label>
              <input
                maxLength={STAGE2_LIMITS.popupText}
                value={stage.end_popup_text || ''}
                onChange={(e) => updateStage({ end_popup_text: e.target.value })}
              />
            </div>
            <p className="fi-admin-hint">Planet images are optional. Each planet message is required (max {STAGE2_MESSAGE_LIMIT} characters).</p>
            {['earth', 'venus', 'moon', 'mars'].map((p) => {
              const messageKey = `${p}_message`;
              const messageValue = stage[messageKey] || '';
              return (
              <div key={p} className="fi-admin-card">
                <h3>{p.charAt(0).toUpperCase() + p.slice(1)}</h3>
                <ImageField label={`${p}_image`} value={stage[`${p}_image`]} onChange={(v) => updateStage({ [`${p}_image`]: v })} />
                <div className="fi-admin-field">
                  <label>
                    {p.charAt(0).toUpperCase() + p.slice(1)} message{' '}
                    <CharCount value={messageValue} max={STAGE2_MESSAGE_LIMIT} />
                  </label>
                  <textarea
                    rows={3}
                    maxLength={STAGE2_MESSAGE_LIMIT}
                    value={messageValue}
                    onChange={(e) => updateStage({ [messageKey]: e.target.value })}
                  />
                </div>
              </div>
            );
            })}
            <SectionSaveBar sectionId="stage_2" />
          </>
        )}

        {stageKey === 'stage_3' && (
          <>
            <div className="fi-admin-field">
              <label>Title <CharCount value={stage.title} max={STAGE3_LIMITS.title} /></label>
              <input
                maxLength={STAGE3_LIMITS.title}
                value={stage.title || ''}
                onChange={(e) => updateStage({ title: e.target.value })}
              />
            </div>
            <div className="fi-admin-field">
              <label>Subtitle <CharCount value={stage.subtitle} max={STAGE3_LIMITS.subtitle} /></label>
              <textarea
                rows={3}
                maxLength={STAGE3_LIMITS.subtitle}
                value={stage.subtitle || ''}
                onChange={(e) => updateStage({ subtitle: e.target.value })}
              />
            </div>
            <div className="fi-admin-field">
              <label>Start popup text <CharCount value={stage.start_popup_text} max={STAGE3_LIMITS.popupText} /></label>
              <input
                maxLength={STAGE3_LIMITS.popupText}
                value={stage.start_popup_text || ''}
                onChange={(e) => updateStage({ start_popup_text: e.target.value })}
              />
            </div>
            <div className="fi-admin-field">
              <label>End popup text <CharCount value={stage.end_popup_text} max={STAGE3_LIMITS.popupText} /></label>
              <input
                maxLength={STAGE3_LIMITS.popupText}
                value={stage.end_popup_text || ''}
                onChange={(e) => updateStage({ end_popup_text: e.target.value })}
              />
            </div>
            <ImageField
              label="Money bag image"
              value={stage.money_bag_image}
              onChange={(v) => updateStage({ money_bag_image: v })}
              compact
            />
            <ImageField
              label="Coin image"
              value={stage.coin_image}
              onChange={(v) => updateStage({ coin_image: v })}
              compact
            />
            <div className="fi-admin-field">
              <label>Base GDP — 1997 (lakh crore INR)</label>
              <input
                type="number"
                min={0}
                step="0.01"
                value={stage.base_gdp ?? ''}
                onChange={(e) => updateStage({ base_gdp: Number(e.target.value) })}
              />
            </div>
            <p className="fi-admin-hint">
              Economy topics: min {STAGE3_MIN_SECTORS}, max {STAGE3_MAX_SECTORS}. Label max {STAGE3_LIMITS.sectorLabel},
              description max {STAGE3_LIMITS.sectorText} characters. Sector image is optional.
              Predicted 2047 GDP = Base GDP × (1 + total allocated % / 100).
            </p>
            {stage.sectors.map((sec, i) => (
              <div key={i} className="fi-admin-card">
                <div className="fi-admin-card-header">
                  <h3>Economy topic {i + 1}</h3>
                  {stage.sectors.length > STAGE3_MIN_SECTORS ? (
                    <button
                      type="button"
                      className="fi-admin-remove-btn"
                      onClick={() => {
                        setStage3({ ...stage, sectors: stage.sectors.filter((_, idx) => idx !== i) });
                      }}
                    >
                      Remove topic
                    </button>
                  ) : null}
                </div>
                <div className="fi-admin-field">
                  <label>
                    Topic label <CharCount value={sec.label} max={STAGE3_LIMITS.sectorLabel} />
                  </label>
                  <input
                    maxLength={STAGE3_LIMITS.sectorLabel}
                    value={sec.label || ''}
                    onChange={(e) => {
                      const sectors = stage.sectors.map((s, idx) =>
                        idx === i ? { ...s, label: e.target.value } : s
                      );
                      setStage3({ ...stage, sectors });
                    }}
                  />
                </div>
                <div className="fi-admin-field">
                  <label>
                    Topic description <CharCount value={sec.text} max={STAGE3_LIMITS.sectorText} />
                  </label>
                  <textarea
                    rows={2}
                    maxLength={STAGE3_LIMITS.sectorText}
                    value={sec.text || ''}
                    onChange={(e) => {
                      const sectors = stage.sectors.map((s, idx) =>
                        idx === i ? { ...s, text: e.target.value } : s
                      );
                      setStage3({ ...stage, sectors });
                    }}
                  />
                </div>
                <ImageField
                  label={`sector${i + 1}_image`}
                  value={sec.image}
                  onChange={(v) => {
                    const sectors = stage.sectors.map((s, idx) =>
                      idx === i ? { ...s, image: v } : s
                    );
                    setStage3({ ...stage, sectors });
                  }}
                />
              </div>
            ))}
            {stage.sectors.length < STAGE3_MAX_SECTORS ? (
              <button
                type="button"
                className="fi-admin-inline-btn"
                onClick={() => {
                  setStage3({
                    ...stage,
                    sectors: [...stage.sectors, defaultStage3Sector(stage.sectors.length)],
                  });
                }}
              >
                + Add economy topic
              </button>
            ) : null}
            <SectionSaveBar sectionId="stage_3" />
          </>
        )}

        {stageKey === 'stage_4' && (
          <>
            <div className="fi-admin-field"><label>Heading</label><input value={stage.heading || ''} onChange={(e) => updateStage({ heading: e.target.value })} /></div>
            <div className="fi-admin-field"><label>Subheading</label><input value={stage.subheading || ''} onChange={(e) => updateStage({ subheading: e.target.value })} /></div>
            <p className="fi-admin-hint">Maps: min {STAGE4_MIN_MAPS}, max {STAGE4_MAX_MAPS}. Saving is blocked below the minimum.</p>
            {(stage.maps || []).map((m, i) => (
              <div key={i} className="fi-admin-card">
                <div className="fi-admin-card-header">
                  <h3>Map {i + 1}</h3>
                  {(stage.maps || []).length > STAGE4_MIN_MAPS ? (
                    <button
                      type="button"
                      className="fi-admin-remove-btn"
                      onClick={() => {
                        const maps = (stage.maps || []).filter((_, idx) => idx !== i);
                        updateStage({ maps });
                      }}
                    >
                      Remove map
                    </button>
                  ) : null}
                </div>
                <div className="fi-admin-field"><label>Map {i + 1} text</label><input value={m.text || ''} onChange={(e) => {
                  const maps = [...(stage.maps || [])];
                  maps[i] = { ...maps[i], text: e.target.value };
                  updateStage({ maps });
                }} /></div>
                <ImageField label={`map${i + 1}_image`} value={m.image} onChange={(v) => {
                  const maps = [...(stage.maps || [])];
                  maps[i] = { ...maps[i], image: v };
                  updateStage({ maps });
                }} />
              </div>
            ))}
            {(stage.maps || []).length < STAGE4_MAX_MAPS ? (
              <button type="button" onClick={() => {
                const maps = [...(stage.maps || [])];
                maps.push({ image: '', text: '', label: String(maps.length + 1) });
                updateStage({ maps });
              }}>+ Add Map</button>
            ) : null}
            <SectionSaveBar sectionId="stage_4" />
          </>
        )}

        {stageKey === 'stage_5' && (
          <>
            <div className="fi-admin-field">
              <label>Start popup text</label>
              <input
                maxLength={STAGE5_LIMITS.popupText}
                value={stage.start_popup_text || ''}
                onChange={(e) => updateStage({ start_popup_text: e.target.value })}
              />
              <CharCount value={stage.start_popup_text} max={STAGE5_LIMITS.popupText} />
            </div>
            <div className="fi-admin-field">
              <label>End popup text</label>
              <input
                maxLength={STAGE5_LIMITS.popupText}
                value={stage.end_popup_text || ''}
                onChange={(e) => updateStage({ end_popup_text: e.target.value })}
              />
              <CharCount value={stage.end_popup_text} max={STAGE5_LIMITS.popupText} />
            </div>
            <div className="fi-admin-field">
              <label>Question text</label>
              <textarea
                rows={2}
                maxLength={STAGE5_LIMITS.question}
                value={stage.question_text || ''}
                onChange={(e) => updateStage({ question_text: e.target.value })}
              />
              <CharCount value={stage.question_text} max={STAGE5_LIMITS.question} />
            </div>
            <div className="fi-admin-field">
              <label>Answer (exact match)</label>
              <input
                maxLength={STAGE5_LIMITS.answer}
                value={stage.answer || ''}
                onChange={(e) => updateStage({ answer: e.target.value })}
              />
              <CharCount value={stage.answer} max={STAGE5_LIMITS.answer} />
            </div>
            <div className="fi-admin-field">
              <label>Hint text</label>
              <textarea
                rows={2}
                maxLength={STAGE5_LIMITS.hint}
                value={stage.hint_text || ''}
                onChange={(e) => updateStage({ hint_text: e.target.value })}
              />
              <CharCount value={stage.hint_text} max={STAGE5_LIMITS.hint} />
            </div>
            <div className="fi-admin-field">
              <label>Placeholder</label>
              <input value={stage.placeholder || ''} onChange={(e) => updateStage({ placeholder: e.target.value })} />
            </div>
            <SectionSaveBar sectionId="stage_5" />
          </>
        )}

        {stageKey === 'stage_6' && (
          <>
            <div className="fi-admin-field">
              <label>Start popup text <CharCount value={stage.start_popup_text} max={STAGE_POPUP_TEXT_LIMIT} /></label>
              <input
                maxLength={STAGE_POPUP_TEXT_LIMIT}
                value={stage.start_popup_text || ''}
                onChange={(e) => updateStage({ start_popup_text: e.target.value })}
              />
            </div>
            <div className="fi-admin-field">
              <label>End popup text <CharCount value={stage.end_popup_text} max={STAGE_POPUP_TEXT_LIMIT} /></label>
              <input
                maxLength={STAGE_POPUP_TEXT_LIMIT}
                value={stage.end_popup_text || ''}
                onChange={(e) => updateStage({ end_popup_text: e.target.value })}
              />
            </div>
            <div className="fi-admin-field">
              <label>Jigsaw title <CharCount value={stage.title} max={STAGE6_TITLE_LIMIT} /></label>
              <input
                maxLength={STAGE6_TITLE_LIMIT}
                value={stage.title || ''}
                onChange={(e) => updateStage({ title: e.target.value })}
              />
            </div>
            <ImageField
              label="puzzle_image"
              value={stage.puzzle_image}
              expectedDims={STAGE6_PUZZLE_IMAGE_DIMS}
              accept="image/png,.png"
              onChange={(v) => updateStage({ puzzle_image: v })}
            />
            <SectionSaveBar sectionId="stage_6" />
          </>
        )}

        {stageKey === 'stage_7' && (
          <>
            <div className="fi-admin-field">
              <label>Title <CharCount value={stage.title} max={STAGE7_LIMITS.title} /></label>
              <input
                maxLength={STAGE7_LIMITS.title}
                value={stage.title || ''}
                onChange={(e) => updateStage({ title: e.target.value })}
              />
            </div>
            <div className="fi-admin-field">
              <label>Start popup text <CharCount value={stage.start_popup_text} max={STAGE7_LIMITS.popupText} /></label>
              <input
                maxLength={STAGE7_LIMITS.popupText}
                value={stage.start_popup_text || ''}
                onChange={(e) => updateStage({ start_popup_text: e.target.value })}
              />
            </div>
            <div className="fi-admin-field">
              <label>End popup text <CharCount value={stage.end_popup_text} max={STAGE7_LIMITS.popupText} /></label>
              <input
                maxLength={STAGE7_LIMITS.popupText}
                value={stage.end_popup_text || ''}
                onChange={(e) => updateStage({ end_popup_text: e.target.value })}
              />
            </div>
            <p className="fi-admin-hint">
              Upload PNG images only. Backgrounds: {STAGE7_MAX_BACKGROUNDS}, class boards: {STAGE7_MAX_CLASS_BOARDS},
              technologies: {STAGE7_MAX_TECHNOLOGIES}. Page background is under Page Backgrounds.
            </p>
            <h3 className="fi-admin-subheading">Mini backgrounds (pick 1 in game)</h3>
            {stage.backgrounds.map((item, i) => (
              <div key={`bg-${i}`} className="fi-admin-card">
                <h3>Background {i + 1}</h3>
                <div className="fi-admin-field">
                  <label>Label <CharCount value={item.label} max={STAGE7_LIMITS.label} /></label>
                  <input
                    maxLength={STAGE7_LIMITS.label}
                    value={item.label || ''}
                    onChange={(e) => updateStage7List('backgrounds', i, { label: e.target.value })}
                  />
                </div>
                <ImageField
                  label={`background${i + 1}_image`}
                  accept="image/png,.png"
                  value={item.image}
                  onChange={(v) => updateStage7List('backgrounds', i, { image: v })}
                />
              </div>
            ))}
            <h3 className="fi-admin-subheading">Class boards (pick 1 in game)</h3>
            {stage.class_boards.map((item, i) => (
              <div key={`board-${i}`} className="fi-admin-card">
                <h3>Class board {i + 1}</h3>
                <div className="fi-admin-field">
                  <label>Label <CharCount value={item.label} max={STAGE7_LIMITS.label} /></label>
                  <input
                    maxLength={STAGE7_LIMITS.label}
                    value={item.label || ''}
                    onChange={(e) => updateStage7List('class_boards', i, { label: e.target.value })}
                  />
                </div>
                <ImageField
                  label={`class_board${i + 1}_image`}
                  accept="image/png,.png"
                  value={item.image}
                  onChange={(v) => updateStage7List('class_boards', i, { image: v })}
                />
              </div>
            ))}
            <h3 className="fi-admin-subheading">Technologies (pick 3 in game)</h3>
            {stage.technologies.map((item, i) => (
              <div key={`tech-${i}`} className="fi-admin-card">
                <h3>Technology {i + 1}</h3>
                <div className="fi-admin-field">
                  <label>Label <CharCount value={item.label} max={STAGE7_LIMITS.label} /></label>
                  <input
                    maxLength={STAGE7_LIMITS.label}
                    value={item.label || ''}
                    onChange={(e) => updateStage7List('technologies', i, { label: e.target.value })}
                  />
                </div>
                <ImageField
                  label={`technology${i + 1}_image`}
                  accept="image/png,.png"
                  value={item.image}
                  onChange={(v) => updateStage7List('technologies', i, { image: v })}
                />
              </div>
            ))}
            <h3 className="fi-admin-subheading">Student (always shown in scene)</h3>
            <div className="fi-admin-card">
              <h3>Student</h3>
              <ImageField
                label="student_image"
                accept="image/png,.png"
                value={stage.student_image}
                onChange={(v) => updateStage({ student_image: v })}
              />
            </div>
            <SectionSaveBar sectionId="stage_7" />
          </>
        )}
      </div>
      <AdminTour steps={TOUR_STEPS} active={tourActive} onClose={closeTour} />
    </div>
  );
}
